import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box, Typography, Button, TextField, Dialog, DialogTitle,
    DialogContent, DialogActions, Grid, MenuItem, Select,
    Paper, Stack, IconButton, Chip, Alert
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AppHeader from '../../components/AppHeader';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';

const OpeningBalances = ({ setIsLoggedIn }) => {
    const [rows, setRows] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [financialYears, setFinancialYears] = useState([]);
    const [selectedYearId, setSelectedYearId] = useState('');
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        id: 'new',
        staff_id: '',
        financial_year_id: '',
        ytd_taxable_earnings: 0,
        ytd_tax_paid: 0,
        ytd_ssb_paid: 0,
        total_relief_claimed: 0
    });

    const gridRef = useRef();

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (selectedYearId) {
            fetchBalances();
        }
    }, [selectedYearId]);

    const fetchInitialData = async () => {
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant');
            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'X-Tenant': tenant
                },
                withCredentials: true
            };

            const [yearsRes, staffRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/financial-years`, config),
                axios.get(`${process.env.REACT_APP_API_URL1}/staff/staff`, config)
            ]);

            if (yearsRes.data.success) {
                setFinancialYears(yearsRes.data.data);
                const activeYear = yearsRes.data.data.find(y => y.is_active === 1);
                if (activeYear) setSelectedYearId(activeYear.id);
            }
            if (staffRes.data) {
                setStaffList(staffRes.data);
            }
        } catch (err) {
            console.error("Fetch error:", err);
            setError("Failed to load initial data.");
        }
    };

    const fetchBalances = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant');
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/opening-balances?financial_year_id=${selectedYearId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'X-Tenant': tenant
                },
                withCredentials: true
            });
            if (res.data.success) {
                setRows(res.data.data);
            }
        } catch (err) {
            setError("Failed to fetch opening balances.");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant');
            const endpoint = formData.id === 'new'
                ? `${process.env.REACT_APP_API_URL1}/sm/payroll/opening-balances`
                : `${process.env.REACT_APP_API_URL1}/sm/payroll/opening-balances/${formData.id}`;

            const method = formData.id === 'new' ? 'post' : 'put';

            const res = await axios[method](endpoint, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'X-Tenant': tenant
                },
                withCredentials: true
            });

            if (res.data.success) {
                setOpenDialog(false);
                fetchBalances();
                setFormData({
                    id: 'new',
                    staff_id: '',
                    financial_year_id: selectedYearId,
                    ytd_taxable_earnings: 0,
                    ytd_tax_paid: 0,
                    ytd_ssb_paid: 0,
                    total_relief_claimed: 0
                });
            }
        } catch (err) {
            setError("Failed to save opening balance.");
        }
    };

    const columnDefs = useMemo(() => [
        { field: 'staff_code', headerName: 'Code', width: 100 },
        { field: 'full_name', headerName: 'Full Name', flex: 1 },
        {
            field: 'ytd_taxable_earnings',
            headerName: 'YTD Taxable Earnings',
            valueFormatter: params => params.value?.toLocaleString()
        },
        {
            field: 'ytd_tax_paid',
            headerName: 'YTD Tax Paid',
            valueFormatter: params => params.value?.toLocaleString()
        },
        {
            field: 'ytd_ssb_paid',
            headerName: 'YTD SSB Paid',
            valueFormatter: params => params.value?.toLocaleString()
        },
        {
            field: 'total_relief_claimed',
            headerName: 'Prior Relief Claimed',
            valueFormatter: params => params.value?.toLocaleString()
        },
        {
            headerName: 'Actions',
            width: 100,
            cellRenderer: (params) => (
                <IconButton size="small" onClick={() => {
                    setFormData(params.data);
                    setOpenDialog(true);
                }}>
                    <EditIcon fontSize="small" />
                </IconButton>
            )
        }
    ], []);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f4f7fe' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ p: 4, flexGrow: 1, overflow: 'auto' }}>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                            Staff Opening Balances (YTD)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage Year-To-Date data for mid-year payroll migrations.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Select
                            size="small"
                            value={selectedYearId}
                            onChange={(e) => setSelectedYearId(e.target.value)}
                            sx={{ minWidth: 200, bgcolor: 'white' }}
                        >
                            {financialYears.map(y => (
                                <MenuItem key={y.id} value={y.id}>{y.year_name}</MenuItem>
                            ))}
                        </Select>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setFormData({
                                    id: 'new',
                                    staff_id: '',
                                    financial_year_id: selectedYearId,
                                    ytd_taxable_earnings: 0,
                                    ytd_tax_paid: 0,
                                    ytd_ssb_paid: 0,
                                    total_relief_claimed: 0
                                });
                                setOpenDialog(true);
                            }}
                            sx={{ bgcolor: '#1e293b', '&:hover': { bgcolor: '#334155' } }}
                        >
                            Add YTD Data
                        </Button>
                    </Stack>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ height: 'calc(100vh - 200px)', width: '100%', borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
                    <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={rows}
                            columnDefs={columnDefs}
                            defaultColDef={{ sortable: true, filter: true, resizable: true }}
                            animateRows={true}
                        />
                    </div>
                </Paper>

                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {formData.id === 'new' ? 'Add Opening Balance' : 'Edit Opening Balance'}
                    </DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={3} sx={{ mt: 0.5 }}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>Staff Member</Typography>
                                <Select
                                    fullWidth
                                    value={formData.staff_id}
                                    onChange={(e) => setFormData({ ...formData, staff_id: e.target.value })}
                                    disabled={formData.id !== 'new'}
                                >
                                    {staffList.map(s => (
                                        <MenuItem key={s.staff_id} value={s.staff_id}>
                                            {s.full_name} ({s.staff_code})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" gutterBottom>YTD Taxable Earnings (MMK)</Typography>
                                <TextField
                                    type="number"
                                    fullWidth
                                    value={formData.ytd_taxable_earnings}
                                    onChange={(e) => setFormData({ ...formData, ytd_taxable_earnings: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" gutterBottom>YTD Tax Paid (MMK)</Typography>
                                <TextField
                                    type="number"
                                    fullWidth
                                    value={formData.ytd_tax_paid}
                                    onChange={(e) => setFormData({ ...formData, ytd_tax_paid: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" gutterBottom>YTD SSB Paid (MMK)</Typography>
                                <TextField
                                    type="number"
                                    fullWidth
                                    value={formData.ytd_ssb_paid}
                                    onChange={(e) => setFormData({ ...formData, ytd_ssb_paid: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" gutterBottom>Prior Relief Claimed (MMK)</Typography>
                                <TextField
                                    type="number"
                                    fullWidth
                                    value={formData.total_relief_claimed}
                                    onChange={(e) => setFormData({ ...formData, total_relief_claimed: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5 }}>
                        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} sx={{ bgcolor: '#1e293b' }}>
                            Save Balance
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
};

export default OpeningBalances;
